import {
  sort,
  SortState,
  SortOption,
  Direction,
  SortOptionSelectItem,
} from '../../utils/sort';
import {
  ListDecoratedMedisanteDevice,
  DevicesSortBy,
  alertsSortingValues,
  StatusesKeys,
  Filter,
} from './types';
import {
  DeviceModels,
  isBluetoothDevice,
  isGatewayDevice,
  MedisanteDevice,
} from '../Device/types';
import { BoundingRect } from 'react-measure';
import { MedisanteTargetSystem } from '../TargetSystems/types';
import dayjs from 'dayjs';
import { allGatewayDevices } from '../Device/constants';
import { AuthUser, Org } from '../../contexts/AuthProvider/types';

export const devicesSortingReducer = (
  state: SortState<ListDecoratedMedisanteDevice>,
  action: SortOption<DevicesSortBy>
): SortState<ListDecoratedMedisanteDevice> => {
  const getDirectionByPrevious = (direction?: Direction) => {
    return direction === Direction.ASC ? Direction.DESC : Direction.ASC;
  };

  switch (action.sortBy) {
    case DevicesSortBy.SERIAL_NUMBER:
      return {
        serialNumber:
          action.direction || getDirectionByPrevious(state.serialNumber),
      };
    case DevicesSortBy.IMEI:
      return {
        imei: action.direction || getDirectionByPrevious(state.imei),
      };
    case DevicesSortBy.MODEL:
      return {
        model: action.direction || getDirectionByPrevious(state.model),
      };
    case DevicesSortBy.STATUS:
      return {
        statuses: action.direction || getDirectionByPrevious(state.statuses),
      };
    case DevicesSortBy.MEASUREMENTS:
      return {
        measurementCount:
          action.direction || getDirectionByPrevious(state.measurementCount),
      };
    case DevicesSortBy.LAST_MEASUREMENT:
      return {
        lastMeasurementAt:
          action.direction || getDirectionByPrevious(state.lastMeasurementAt),
      };
    case DevicesSortBy.LAST_SYNC:
      return {
        lastCommunicationAt:
          action.direction || getDirectionByPrevious(state.lastCommunicationAt),
      };
    case DevicesSortBy.MAC:
      return {
        mac: action.direction || getDirectionByPrevious(state.mac),
      };

    default:
      return state;
  }
};

export const isBehindSyncingSchedule = (device: MedisanteDevice, now: Date) => {
  if (isMuted(device)) {
    return false;
  }
  // bluetooth does not have nextCommunicationAt
  if (isBluetoothDevice(device.model)) {
    return false;
  }
  if (!device.nextCommunicationAt) {
    return false;
  }
  const gracePeriod = 5 * 60 * 1000;
  const nextCommunicationAt = new Date(device.nextCommunicationAt);
  const nextCommunicationAtWithGracePeriod = new Date(
    nextCommunicationAt.getTime() + gracePeriod
  );
  return nextCommunicationAtWithGracePeriod < now;
};

export const hasLowBattery = (
  device: MedisanteDevice,
  avg: boolean = false
) => {
  const value = avg
    ? device.batteryVoltageAverageHealth
    : device.batteryVoltageHealth;
  if (!value) {
    return false;
  }
  // bluetooth does not have battery
  if (isBluetoothDevice(device.model)) {
    return false;
  }
  return value === 'warning';
};

export const hasMinBattery = (
  device: MedisanteDevice,
  avg: boolean = false
) => {
  const value = avg
    ? device.batteryVoltageAverageHealth
    : device.batteryVoltageHealth;
  if (!value) {
    return false;
  }
  // bluetooth does not have battery
  if (isBluetoothDevice(device.model)) {
    return false;
  }
  return value === 'critical';
};

export const hasLowSignalStrength = (
  device: MedisanteDevice,
  avg: boolean = false
) => {
  const value = avg
    ? device.signalStrengthAverageHealth
    : device.signalStrengthHealth;
  if (!value) {
    return false;
  }
  if (isBluetoothDevice(device.model)) {
    return false;
  }
  return value === 'warning';
};

export const hasMinSignalStrength = (
  device: MedisanteDevice,
  avg: boolean = false
) => {
  const value = avg
    ? device.signalStrengthAverageHealth
    : device.signalStrengthHealth;
  if (!value) {
    return false;
  }
  // bluetooth does not have signal strength
  if (isBluetoothDevice(device.model)) {
    return false;
  }
  return value === 'critical';
};

export const hasPendingChanges = (device: MedisanteDevice) => {
  if (isMuted(device)) {
    return false;
  }
  // bluetooth does not have pending changes
  if (isBluetoothDevice(device.model)) {
    return false;
  }
  if (!device.lastCommunicationAt) {
    return true;
  }
  const lastCommunicationAt = new Date(device.lastCommunicationAt);
  const updatedAt = new Date(device.updatedAt);
  return lastCommunicationAt < updatedAt;
};

export const hasNoTargetSystem = (
  device: MedisanteDevice,
  allowUnlinkedGateway: Boolean
) => {
  if (isMuted(device)) {
    return false;
  }
  if (isGatewayDevice(device.model) && allowUnlinkedGateway) {
    // check if unlinked gateway is enabled
    return false;
  }
  if (!device.targetSystems) return true;
  return device.targetSystems.length === 0;
};

export const isMuted = (device: MedisanteDevice) => {
  if (!device.properties) return false;
  return device.properties.muted && device.properties.muted.length > 0;
};

export const decorateDeviceWithStatuses = (
  device: MedisanteDevice,
  authUser: AuthUser
): ListDecoratedMedisanteDevice => {
  let allowUnlinkedGateway = false;
  if (device.allowUnlinkedGateway) {
    allowUnlinkedGateway = true;
  }
  return {
    ...device,
    selected: false,
    statuses: {
      isBehindSyncingSchedule: isBehindSyncingSchedule(device, new Date()),
      hasLowBattery: hasLowBattery(device),
      hasMinBattery: hasMinBattery(device),
      hasLowSignalStrength: hasLowSignalStrength(device),
      hasMinSignalStrength: hasMinSignalStrength(device),
      hasPendingChanges: hasPendingChanges(device),
      hasNoTargetSystem: hasNoTargetSystem(device, allowUnlinkedGateway),
      hasAvgLowBattery: hasLowBattery(device, true),
      hasAvgMinBattery: hasMinBattery(device, true),
      hasAvgLowSignalStrength: hasLowSignalStrength(device, true),
      hasAvgMinSignalStrength: hasMinSignalStrength(device, true),
    },
  };
};

export const hasConfigurationWarning = (
  device: MedisanteDevice,
  targetSystems: MedisanteTargetSystem[] = []
) => {
  if (!device.targetSystems) return false;
  if (!targetSystems) return false;
  let ts = targetSystems.filter((ts) => {
    // if (
    //   device.targetSystems.indexOf(ts.id) !== -1 &&
    //   ts.format &&
    //   ts.format.match(/fhir/i)
    // ) {
    //   return true;
    // }
    return false;
  });

  return ts.length > 0;
};

export const decorateListWithStatuses = (
  devices: MedisanteDevice[],
  authUser: AuthUser
): ListDecoratedMedisanteDevice[] => {
  return devices.map((d) => decorateDeviceWithStatuses(d, authUser));
};

export const sortDevices = (
  devices: ListDecoratedMedisanteDevice[],
  sortState: SortState<ListDecoratedMedisanteDevice>,
  orgs: Org[],
  totalView: boolean
) => {
  const getNumberOfStatusesForDevice = (
    device: ListDecoratedMedisanteDevice
  ): number => {
    if (device.deactivated) {
      return -2;
    }
    if (device.isArchived) {
      return -1;
    }
    let num = 0;
    if (device.properties && device.properties.testdevice) {
      num += 1;
    }
    if (device.properties && device.properties.status) {
      num += 1;
    }
    if (device.lastStatusCode > 0) {
      num += 1;
    }
    if (device.allowUnlinkedGateway) {
      return num;
    }
    num +=
      !!device && !!device.statuses
        ? Object.values(device.statuses).filter((s) => s).length
        : 0;
    if (device.batteryVoltageHealth === 'ok') {
      num += 1;
    }
    if (device.signalStrengthHealth === 'ok') {
      num += 1;
    }
    return num;
  };

  const getStatusCombination = (
    device: ListDecoratedMedisanteDevice
  ): number => {
    if (device.isArchived) {
      return -1;
    }
    const num =
      !!device && !!device.statuses
        ? Object.entries(device.statuses)
            .map(([key, value]) => {
              const statusKey = key as StatusesKeys;
              return value ? alertsSortingValues[statusKey] : 0;
            })
            .reduce((sum, sortingValue) => sum + sortingValue, 0)
        : 0;

    return num;
  };
  const sortStateCopy = {
    ...sortState,
  };
  return sort(devices, sortStateCopy, {
    serialNumber: (d) => d.serialNumber,
    imei: (d) => d.imei,
    model: (d) => d.model,
    isPinned: (d) => (!!d.isPinned ? 1 : 0),
    statuses: [getNumberOfStatusesForDevice, getStatusCombination],
    measurementCount: (d) =>
      d.model === 'GW9017' || d.model === 'SmartLink'
        ? -1
        : (totalView ? d.measurementCount : d.measurementTripCount) || 0,
    lastMeasurementAt: (d) => {
      // Gateway have hidden measurements we need show it as that is no measurements for gateway
      if (allGatewayDevices.indexOf(d.model as DeviceModels) > -1) {
        return dayjs().subtract(11, 'years').toDate();
      }

      return !!d.lastMeasurementAt
        ? dayjs(d.lastMeasurementAt).toDate()
        : dayjs().subtract(10, 'years').toDate();
    },
    lastCommunicationAt: (d) => {
      return !!d.lastCommunicationAt
        ? dayjs(d.lastCommunicationAt).toDate()
        : dayjs().subtract(10, 'years').toDate();
    },
  });
};

export const mobileSortOptions: SortOptionSelectItem<DevicesSortBy>[] = [
  {
    value: { sortBy: DevicesSortBy.SERIAL_NUMBER, direction: Direction.ASC },
    label: 'Serial Number (ascending)',
    id: 'serial-number-asc',
  },
  {
    value: { sortBy: DevicesSortBy.SERIAL_NUMBER, direction: Direction.DESC },
    label: 'Serial Number (descending)',
    id: 'serial-number-desc',
  },
  {
    value: { sortBy: DevicesSortBy.IMEI, direction: Direction.ASC },
    label: 'IMEI (ascending)',
    id: 'imei-asc',
  },
  {
    value: { sortBy: DevicesSortBy.IMEI, direction: Direction.DESC },
    label: 'IMEI (descending)',
    id: 'imei-desc',
  },
  {
    value: {
      sortBy: DevicesSortBy.MODEL,
      direction: Direction.ASC,
    },
    label: 'Model (ascending)',
    id: 'model-asc',
  },
  {
    value: {
      sortBy: DevicesSortBy.MODEL,
      direction: Direction.DESC,
    },
    label: 'Model (descending)',
    id: 'model-desc',
  },
  {
    value: {
      sortBy: DevicesSortBy.STATUS,
      direction: Direction.ASC,
    },
    label: 'Status (ascending)',
    id: 'status-asc',
  },
  {
    value: {
      sortBy: DevicesSortBy.STATUS,
      direction: Direction.DESC,
    },
    label: 'Status (descending)',
    id: 'status-desc',
  },
  {
    value: {
      sortBy: DevicesSortBy.MEASUREMENTS,
      direction: Direction.ASC,
    },
    label: 'Measurements (ascending)',
    id: 'measurements-asc',
  },
  {
    value: {
      sortBy: DevicesSortBy.MEASUREMENTS,
      direction: Direction.DESC,
    },
    label: 'Measurements (descending)',
    id: 'measurements-desc',
  },
  {
    value: {
      sortBy: DevicesSortBy.LAST_MEASUREMENT,
      direction: Direction.ASC,
    },
    label: 'Last measurement (ascending)',
    id: 'last-measurement-asc',
  },
  {
    value: {
      sortBy: DevicesSortBy.LAST_MEASUREMENT,
      direction: Direction.DESC,
    },
    label: 'Last measurement (descending)',
    id: 'last-measurement-desc',
  },
  {
    value: {
      sortBy: DevicesSortBy.LAST_SYNC,
      direction: Direction.ASC,
    },
    label: 'Last sync (ascending)',
    id: 'last-sync-asc',
  },
  {
    value: {
      sortBy: DevicesSortBy.LAST_SYNC,
      direction: Direction.DESC,
    },
    label: 'Last sync (descending)',
    id: 'last-sync-desc',
  },
  {
    value: {
      sortBy: DevicesSortBy.MAC,
      direction: Direction.ASC,
    },
    label: 'MAC (ascending)',
    id: 'mac-asc',
  },
  {
    value: {
      sortBy: DevicesSortBy.MAC,
      direction: Direction.DESC,
    },
    label: 'MAC (descending)',
    id: 'mac-desc',
  },
];

export const getBoundingRectWidth = (size: BoundingRect | undefined) =>
  size ? size.width : 0;
export const getBoundingRectHeight = (size: BoundingRect | undefined) =>
  size ? size.height : 0;

export const devicesInCSVFormat = (devices: MedisanteDevice[]) => {
  let items = 'Model,Serial number,IMEI,ICCID,MAC\n';

  devices.forEach((item) => {
    items += `${item.model},${item.serialNumber},${item.imei},${
      item.iccid ?? ''
    },${item.mac || ''}\n`;
  });

  return items;
};

export const validateFilters = (filters: Filter[]) => {
  const filtersThatCanBeLessThen3CharsLong = [
    'measurementGreaterThan',
    'measurementLessThan',
    'measurementEquals',
    'battery_voltage_health',
    'signal_strength_health',
    'battery_voltage_average_health',
    'signal_strength_average_health',
    'model',
    'version',
    'measurementType',
  ];

  return filters
    .filter((f) => !filtersThatCanBeLessThen3CharsLong.includes(f.category))
    .every((f) => f.value.length > 3);
};
